import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { focusFirstError } from "@/lib/formA11y";
import { formatWhen } from "@/lib/format";
import { newIdempotencyKey } from "@/lib/idempotency";
import {
  ApiClientError,
  type CapacityProduct,
  type CapacityRequest,
  type TenantEnvironment,
} from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { Field, TextArea, TextInput } from "@/components/Field";
import { BarChart, ClipboardList, Cloud, Cpu, FolderKanban, ListTodo, Plug } from "@/components/icons";
import { LoadList } from "@/components/LoadList";
import { StatePanel } from "@/components/StatePanel";
import { useLocale } from "@/locale/LocaleContext";
import { useOnline } from "@/lib/offline";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";
import { EnvironmentIndexCabinet } from "./EnvironmentSaveStudies";
import styles from "./environment.module.css";

const RESOURCE_FIELDS = ["name", "kind", "product", "state", "status", "id"] as const;

function resourceKey(item: unknown, index: number): string {
  if (item && typeof item === "object" && !Array.isArray(item)) {
    const id = (item as Record<string, unknown>).id;
    if (typeof id === "string" && id.trim()) return id;
  }
  return `resource-${index}`;
}

function describeResource(item: unknown, fallback: string): string {
  if (!item || typeof item !== "object" || Array.isArray(item)) return fallback;
  const record = item as Record<string, unknown>;
  const parts: string[] = [];
  for (const key of RESOURCE_FIELDS) {
    const value = record[key];
    if (typeof value === "string" && value.trim() && value.length <= 120) {
      parts.push(`${key}: ${value.trim()}`);
    }
  }
  return parts.length ? parts.join(" · ") : fallback;
}

function productLabel(
  id: CapacityProduct,
  labels: { storage: string; archive: string; backup: string },
): string {
  if (id === "project-storage") return labels.storage;
  if (id === "archive") return labels.archive;
  return labels.backup;
}

export function EnvironmentPage() {
  const { m, locale } = useLocale();
  const online = useOnline();
  const [data, setData] = useState<TenantEnvironment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [product, setProduct] = useState<CapacityProduct>("project-storage");
  const [amount, setAmount] = useState("100");
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<CapacityRequest | null>(null);
  const idemRef = useRef(newIdempotencyKey());

  async function load() {
    setLoading(true);
    try {
      const env = await api.environment();
      setData(env);
      setError(null);
      const first = env.catalog[0]?.id;
      if (first) setProduct(first);
    } catch (e) {
      setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function bumpIdempotency() {
    idemRef.current = newIdempotencyKey();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!online) {
      setFormError(m.common.onlineRequired);
      return;
    }
    const next: Record<string, string> = {};
    const parsed = Number(amount);
    if (!amount.trim()) next["cap-amount"] = m.environment.amountRequired;
    else if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100000) {
      next["cap-amount"] = m.environment.amountRange;
    }
    if (notes.length > 2000) next["cap-notes"] = m.environment.notesHint;
    setFieldErrors(next);
    if (Object.keys(next).length) {
      queueMicrotask(() => focusFirstError(next, ["cap-amount", "cap-notes"]));
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      await api.createCapacityRequest(
        {
          product,
          amount_gb: parsed,
          notes: notes.trim() ? notes.trim() : undefined,
        },
        idemRef.current,
      );
      setNotes("");
      bumpIdempotency();
      await load();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setFormError(m.environment.conflict);
        bumpIdempotency();
      } else {
        setFormError(err instanceof Error ? err.message : m.environment.submitFailed);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onCancel() {
    if (!cancelTarget) return;
    setBusy(true);
    setFormError(null);
    try {
      await api.cancelCapacityRequest(cancelTarget.id);
      setCancelTarget(null);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : m.environment.cancelFailed);
    } finally {
      setBusy(false);
    }
  }

  const labels = {
    storage: m.environment.productStorage,
    archive: m.environment.productArchive,
    backup: m.environment.productBackup,
  };
  const requestStates = ["requested", "canceled"] as const;
  const requestMix = requestStates
    .map((state) => ({
      state,
      count: data?.requests.filter((req) => req.state === state).length ?? 0,
    }))
    .filter((item) => item.count > 0);
  const requestMax = Math.max(1, ...requestMix.map((item) => item.count));

  if (loading && !data && !error) return <LoadList title={m.environment.title} rows={4} />;

  return (
    <AuthStatePanels loading={false} error={error}>
      {!data ? (
        <StatePanel kind="empty" title={m.states.empty} />
      ) : (
        <div>
          <h1 className={shell.pageTitle}>{m.environment.title}</h1>
          <p className={shell.pageLead} style={{ marginBottom: "0.75rem" }}>
            {m.environment.lead}
          </p>
          {data.capabilities.storage_provisioning ? (
            <p className={ui.meta} style={{ marginBottom: "0.75rem" }}>
              {m.environment.storageProvisioningOn}
            </p>
          ) : null}
          <EnvironmentIndexCabinet
            facts={{
              liveCompute: data.capabilities.live_compute,
              storageProvisioning: data.capabilities.storage_provisioning,
              paidCheckout: data.capabilities.paid_checkout,
              activeDemoLeases: data.summary.active_demo_leases,
              openArchiveRequests: data.requests.filter(
                (req) =>
                  (req.product === "archive" || req.product === "backup") && req.state !== "canceled",
              ).length,
            }}
          />

          <h2 className={`${styles.sectionHead} ${ui.sectionHead} ${styles.afterCabinet}`}>
            <BarChart size={18} aria-hidden />
            {m.environment.activityTitle}
          </h2>
          <div className={shell.statGrid} aria-label={m.environment.activityTitle}>
            <Link className={shell.stat} to="/app/projects">
              <FolderKanban size={18} aria-hidden />
              <strong>{data.summary.projects}</strong>
              <span>{m.environment.projects}</span>
            </Link>
            <Link className={shell.stat} to="/app/tasks">
              <ListTodo size={18} aria-hidden />
              <strong>{data.summary.queued_tasks}</strong>
              <span>{m.environment.queued}</span>
            </Link>
            <Link className={shell.stat} to="/app/tasks">
              <BarChart size={18} aria-hidden />
              <strong>{data.summary.running_tasks}</strong>
              <span>{m.environment.running}</span>
            </Link>
            <Link className={shell.stat} to="/app/tasks">
              <ClipboardList size={18} aria-hidden />
              <strong>{data.summary.completed_tasks}</strong>
              <span>{m.environment.completed}</span>
            </Link>
            <Link className={shell.stat} to="/app/integrations">
              <Plug size={18} aria-hidden />
              <strong>{data.summary.connections}</strong>
              <span>{m.environment.connections}</span>
            </Link>
            <Link
              className={shell.stat}
              to="/app/machines"
              aria-label={`${m.environment.demoLeases}. ${m.environment.demoLeasesHint}`}
              title={m.environment.demoLeasesHint}
            >
              <Cpu size={18} aria-hidden />
              <strong>{data.summary.active_demo_leases}</strong>
              <span>{m.environment.demoLeases}</span>
            </Link>
          </div>
          {data.capabilities.live_compute ? null : (
            <p className={`${ui.meta} ${styles.capabilityNote}`}>{m.environment.liveComputeOff}</p>
          )}

          <div className={`${ui.panel} ${styles.resourcesPanel}`}>
            <h2>
              <Cloud size={18} aria-hidden />
              {m.environment.resourcesTitle}
            </h2>
            {data.resources.length === 0 ? (
              <p>
                {data.capabilities.storage_provisioning
                  ? m.environment.resourcesEmptyProvisioning
                  : m.environment.resourcesEmpty}
              </p>
            ) : (
              <>
                <p>{m.environment.resourceIntro.replace("{n}", String(data.resources.length))}</p>
                <ul className={styles.resourceList}>
                  {data.resources.map((item, index) => (
                    <li key={resourceKey(item, index)}>
                      <Cloud size={16} aria-hidden />
                      <span>{describeResource(item, m.environment.resourceUndescribed)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <h2 className={`${styles.sectionHead} ${ui.sectionHead}`}>
            <ClipboardList size={18} aria-hidden />
            {m.environment.catalogTitle}
          </h2>
          <p className={ui.meta} style={{ margin: "0 0 0.65rem" }}>
            {m.environment.catalogLead}
          </p>
          <ul className={styles.catalogList}>
            {data.catalog.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={styles.catalogItem}
                  aria-pressed={product === item.id}
                  onClick={() => {
                    setProduct(item.id);
                    bumpIdempotency();
                  }}
                >
                  <span className={styles.catalogLead}>
                    {item.id === "project-storage" ? (
                      <FolderKanban size={18} aria-hidden />
                    ) : (
                      <Cloud size={18} aria-hidden />
                    )}
                    <strong>{productLabel(item.id, labels)}</strong>
                  </span>
                  <span className={styles.catalogMeta}>
                    {item.availability === "request"
                      ? m.environment.availabilityRequest
                      : item.availability}
                    {" · "}
                    {m.environment.paymentNotConnected}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <form
            noValidate
            onSubmit={onSubmit}
            className={ui.panel}
            style={{ display: "grid", gap: "0.75rem", marginBottom: "1.25rem" }}
          >
            <h2 className={`${styles.sectionHead} ${ui.sectionHead}`} style={{ margin: 0 }}>
              <ClipboardList size={18} aria-hidden />
              {m.environment.requestTitle}
            </h2>
            <Field label={m.environment.product} htmlFor="cap-product">
              <select
                id="cap-product"
                className={ui.input}
                value={product}
                onChange={(e) => {
                  setProduct(e.target.value as CapacityProduct);
                  bumpIdempotency();
                }}
              >
                {data.catalog.map((item) => (
                  <option key={item.id} value={item.id}>
                    {productLabel(item.id, labels)}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label={m.environment.amount}
              htmlFor="cap-amount"
              error={fieldErrors["cap-amount"]}
              hint={m.environment.amountHint}
            >
              <TextInput
                inputMode="numeric"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  bumpIdempotency();
                }}
              />
            </Field>
            <Field
              label={m.environment.notes}
              htmlFor="cap-notes"
              error={fieldErrors["cap-notes"]}
              hint={m.environment.notesHint}
            >
              <TextArea
                className="resize-none"
                style={{ resize: "none" }}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  bumpIdempotency();
                }}
              />
            </Field>
            {formError ? (
              <p className={ui.fieldError} role="alert">
                {formError}
              </p>
            ) : null}
            <Button type="submit" disabled={busy || !online} aria-busy={busy || undefined}>
              {busy ? m.environment.submitting : m.environment.submit}
            </Button>
          </form>

          <h2 className={`${styles.sectionHead} ${ui.sectionHead}`}>
            <ListTodo size={18} aria-hidden />
            {m.environment.historyTitle}
          </h2>
          {requestMix.length > 0 ? (
            <section aria-label={m.environment.requestStates}>
              <p className={ui.meta}>{m.environment.requestStatesNote}</p>
              <div className={shell.mix}>
                {requestMix.map((item) => (
                  <div key={item.state} className={shell.mixRow}>
                    <span>
                      {item.state === "canceled" ? m.environment.stateCanceled : m.environment.stateRequested}
                    </span>
                    <span className={shell.mixTrack}>
                      <span
                        className={shell.mixFill}
                        data-state={item.state}
                        style={{ width: `${(item.count / requestMax) * 100}%` }}
                      />
                    </span>
                    <span className={shell.mixCount}>{item.count}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          {data.requests.length === 0 ? (
            <p className={ui.meta}>{m.environment.historyEmpty}</p>
          ) : (
            data.requests.map((req) => (
              <div key={req.id} className={styles.requestRow}>
                <span className={styles.requestIcon}>
                  <ClipboardList size={18} aria-hidden />
                </span>
                <div>
                  <strong>
                    {productLabel(req.product, labels)} · {req.amount_gb} {m.environment.unitGb}
                  </strong>
                  <div className={ui.meta}>
                    {req.state === "canceled"
                      ? m.environment.stateCanceled
                      : m.environment.stateRequested}{" "}
                    · {formatWhen(req.created_at, locale)}
                  </div>
                  {req.notes ? <div className={ui.meta}>{req.notes}</div> : null}
                  {req.state === "requested" ? (
                    <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "0.5rem" }}>
                      <Button
                        variant="danger"
                        onClick={() => {
                          setFormError(null);
                          setCancelTarget(req);
                        }}
                      >
                        {m.environment.cancel}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}

          <ConfirmSheet
            open={Boolean(cancelTarget)}
            title={m.environment.cancelTitle}
            confirmLabel={m.environment.cancelConfirm}
            danger
            busy={busy}
            onClose={() => setCancelTarget(null)}
            onConfirm={() => void onCancel()}
          >
            <p>{m.environment.cancelBody}</p>
            {cancelTarget ? (
              <p className={ui.meta}>
                {productLabel(cancelTarget.product, labels)} · {cancelTarget.amount_gb}{" "}
                {m.environment.unitGb}
              </p>
            ) : null}
          </ConfirmSheet>
        </div>
      )}
    </AuthStatePanels>
  );
}
